import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../redux/store';
import { fetchDataLst, fetchTrapInfo, updateTrap, changeInsectCount, getScrollInfoV2, deleteTrapPush, cleanTrap, deleteCurrentData, deleteAllData, removeTrap } from '../redux/slices/dataSlice';
import fallbackIcon from '../assets/images/icon.png';
import BatteryIndicator from './BatteryIndicator';
import WifiIndicator from './WifiIndicator';
import InsectCountIndicator from './InsectCountIndicator';
import { useTranslation } from 'react-i18next';
import { logger } from '../utils/logger';

interface TrapInfoResponse {
  trap: {
    id: string;
    trapName: string;
    location: string;
    building: string;
    macAddress: string;
    probeModel: string;
    frequency: string;
    brightness: string;
    pwm: string;
    minutes: string;
    hours: string;
    days: string;
    subscriptionData: {
      subscriptionType: string;
    };
    yellow: number;
    lightRed: number;
    darkRed: number;
  };
}

interface ProbeDataViewProps {
  probeId: string | null;
  teamName: string;
  trapInfo?: TrapInfoResponse | null;
}



interface ProbeDataItem {
  insectCollection?: {
    beetle?: number | null;
    moth?: number | null;
    weevil?: number | null;
    other?: number | null;
  } | null;
  trapId: string;
  building: string | null;
  location: string | null;
  humidity: string;
  minHumidity: string;
  maxHumidity: string;
  avgHumidity: string;
  temperature: string;
  minTemperature: string;
  maxTemperature: string;
  avgTemperature: string;
  particleCount: number;
  timestamp: string;
  previousDateTimeStamp: string;
  wifiInfo: string;
  batteryInfo: string;
  probeModel: string;
  attempts: number;
  fails: number;
  accessLogin: number;
  active: boolean;
  session: string;
  cameraError: string;
  firmwareVersion: string;
  serverError: string;
  wifiError: string;
  serverAttempts: string;
  consecutiveServerFails: string;
  serverConnectionLost: string;
  wakeupCount: string;
  prevTime: string;
  encryptionType: string;
  openVariable: string;
  failedRSSI: string;
  otaSuccess: string;
  rapidFire: string;
  cameraFirmwareVersion: string;
  indexTimerCycle: number;
  lastPadChange: string | null;
  scheduledImage: number;
  _id: string;
  fileName: string;
  originalFileUrl: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

interface ProbeDataResponse {
  trap: {
    dataLst: ProbeDataItem[];
  };
}




interface EditModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  currentValue: string;
  onSave: (value: string) => void;
}

interface EditInsectCountModalProps {
  isOpen: boolean;
  onClose: () => void;
  insectType: 'beetle' | 'moth' | 'weevil' | 'other';
  currentCount: number;
  trapId: string;
  fileName: string;
  onSave: () => void;
}

interface DeleteConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  probeId: string;
  currentData: ProbeDataItem | null;
}

interface FunctionConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (sessionName?: string) => Promise<void> | void;
  title: string;
  description: string;
  probeId: string;
  requiresSessionName?: boolean;
}

// Brightness and Frequency scales are now dynamically generated with translations inside the component

const EditModal = ({ isOpen, onClose, title, currentValue, onSave }: EditModalProps) => {
  const [value, setValue] = useState(currentValue || '');
  const { t } = useTranslation(['common', 'dashboard']);

  useEffect(() => {
    setValue(currentValue || '');
  }, [currentValue]);

  if (!isOpen) return null;

  const isFrequency = title === 'Frequency' || title === t('dashboard:frequency');
  const isBrightness = title === 'Brightness' || title === t('dashboard:brightness');
  
  // Generate scales with translations
  const BrightnessScale = [
    { label: t('dashboard:level1'), value: '1' },
    { label: t('dashboard:level2'), value: '2' },
    { label: t('dashboard:level3'), value: '3' },
    { label: t('dashboard:level4'), value: '4' },
    { label: t('dashboard:level5'), value: '5' },
  ];

  const FrequencyScale = [
    { label: t('dashboard:5minutes'), value: '1' },
    { label: t('dashboard:4hours'), value: '6' },
    { label: t('dashboard:8hours'), value: '2' },
    { label: t('dashboard:24hours'), value: '3' },
    { label: t('dashboard:48hours'), value: '4' },
  ];
  
  const options = isFrequency ? FrequencyScale : isBrightness ? BrightnessScale : null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-96">
        <h3 className="text-lg font-semibold mb-4">{t('common:edit')} {title}</h3>
        {options ? (
          <select
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="w-full p-2 border rounded mb-4"
          >
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        ) : (
          <input
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="w-full p-2 border rounded mb-4"
          />
        )}
        <div className="flex justify-end space-x-2">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-900 rounded hover:bg-gray-300 cursor-pointer"
          >
            {t('common:cancel')}
          </button>
          <button
            onClick={() => {
              onSave(value);
              onClose();
            }}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 cursor-pointer"
          >
            {t('common:save')}
          </button>
        </div>
      </div>
    </div>
  );
};

const EditInsectCountModal = ({ 
  isOpen, 
  onClose, 
  insectType, 
  currentCount, 
  trapId, 
  fileName,
  onSave 
}: EditInsectCountModalProps) => {
  const [count, setCount] = useState(currentCount.toString());
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation(['common', 'dashboard']);

  useEffect(() => {
    setCount(currentCount.toString());
  }, [currentCount]);

  if (!isOpen) return null;

  const handleSave = async () => {
    try {
      const payload = {
        fileName: fileName,
        trapId: trapId.toString(),
        newCount: parseInt(count),
        insect: insectType
      };
      await dispatch(changeInsectCount(payload)).unwrap();
      onSave();
      onClose();
    } catch (error) {
      logger.error('Failed to update insect count:', error);
    }
  };

  const getInsectTypeName = () => {
    if (insectType === 'beetle') return t('dashboard:beetles');
    if (insectType === 'moth') return t('dashboard:moths');
    if (insectType === 'weevil') return t('dashboard:weevils');
    return t('dashboard:other');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-96">
        <h3 className="text-lg font-semibold mb-4">{t('common:edit')} {getInsectTypeName()} {t('dashboard:insectCount')}</h3>
        <input
          type="number"
          value={count}
          onChange={(e) => setCount(e.target.value)}
          className="w-full p-2 border rounded mb-4"
          min="0"
        />
        <div className="flex justify-end space-x-2">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-900 rounded hover:bg-gray-300 cursor-pointer"
          >
            {t('common:cancel')}
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 cursor-pointer"
          >
            {t('common:save')}
          </button>
        </div>
      </div>
    </div>
  );
};

const DeleteConfirmationModal = ({ isOpen, onClose, onConfirm, probeId, currentData }: DeleteConfirmationModalProps) => {
  const { t } = useTranslation(['dashboard', 'common']);
  
  if (!isOpen) return null;

  const formattedDate = currentData?.createdAt 
    ? new Date(currentData.createdAt).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric'
      })
    : 'N/A';

  const formattedTime = currentData?.createdAt
    ? new Date(currentData.createdAt).toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })
    : 'N/A';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
        <h3 className="text-lg font-semibold mb-4">{t('dashboard:confirmDelete')}</h3>
        <div className="space-y-4 mb-6">
          <p className="text-gray-900">
            {t('dashboard:confirmDeleteMessage')}
          </p>

          {/* Data Information */}
          <div className="bg-gray-50 p-4 rounded-md">
            <div className="space-y-2">
              <p className="text-sm text-gray-900">
                <span className="font-medium">{t('dashboard:smartProbeId')}:</span> {probeId}
              </p>
              <p className="text-sm text-gray-900">
                <span className="font-medium">{t('dashboard:date')}:</span> {formattedDate}
              </p>
              <p className="text-sm text-gray-900">
                <span className="font-medium">{t('dashboard:time')}:</span> {formattedTime}
              </p>
            </div>
          </div>
        </div>
        <div className="flex justify-end space-x-4">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-900 hover:text-gray-800 font-medium cursor-pointer"
          >
            {t('common:cancel')}
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 font-medium cursor-pointer"
          >
            {t('common:delete')}
          </button>
        </div>
      </div>
    </div>
  );
};

const FunctionConfirmationModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title, 
  description, 
  probeId,
  requiresSessionName = false 
}: FunctionConfirmationModalProps) => {
  const { t } = useTranslation(['dashboard', 'common']);
  const [sessionName, setSessionName] = useState('');
  const [isValid, setIsValid] = useState(true);

  const handleSessionNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Only allow letters and numbers
    const valid = /^[a-zA-Z0-9]*$/.test(value);
    setSessionName(value);
    setIsValid(valid);
  };

  const handleConfirm = () => {
    if (requiresSessionName) {
      if (!sessionName.trim() || !isValid) {
        return;
      }
      onConfirm(sessionName);
    } else {
      onConfirm();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-100 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-xl">
        <h3 className="text-lg font-semibold mb-2">{title}</h3>
        <p className="text-gray-900 mb-4">{description}</p>
        <p className="text-sm text-gray-800 mb-4">{t('dashboard:smartProbeId')}: {probeId}</p>
        
        {requiresSessionName && (
          <div className="mb-4">
            <label htmlFor="sessionName" className="block text-sm font-medium text-gray-900 mb-1 cursor-pointer">
              {t('dashboard:sessionName')}
            </label>
            <input
              type="text"
              id="sessionName"
              value={sessionName}
              onChange={handleSessionNameChange}
              className={`w-full p-2 border rounded cursor-pointer ${
                !isValid ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder={t('dashboard:enterSessionName')}
            />
            {!isValid && (
              <p className="text-red-500 text-sm mt-1">
                {t('dashboard:onlyLettersNumbers')}
              </p>
            )}
          </div>
        )}
        
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-900 hover:text-gray-800 cursor-pointer"
          >
            {t('common:cancel')}
          </button>
          <button
            onClick={handleConfirm}
            className={`px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 cursor-pointer ${
              requiresSessionName && (!sessionName.trim() || !isValid) ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            disabled={requiresSessionName && (!sessionName.trim() || !isValid)}
          >
            {t('dashboard:confirm')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default function ProbeDataView({ probeId, teamName, trapInfo: propTrapInfo }: ProbeDataViewProps) {
  const [probeData, setProbeData] = useState<ProbeDataResponse | null>(null);
  const [localTrapInfo, setLocalTrapInfo] = useState<TrapInfoResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { t, i18n } = useTranslation(['dashboard', 'common']);
  const [currentDataIndex, setCurrentDataIndex] = useState(0);
  const [isCountExpanded, setIsCountExpanded] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isMoreInfoExpanded, setIsMoreInfoExpanded] = useState(false);
  const imageRef = useRef<HTMLImageElement>(null);

  // Sync propTrapInfo to localTrapInfo when prop changes
  useEffect(() => {
    if (propTrapInfo) {
      setLocalTrapInfo(propTrapInfo);
    }
  }, [propTrapInfo]);

  // Always use localTrapInfo for display (so updates are reflected immediately)
  const trapInfo = localTrapInfo;
  const [editModal, setEditModal] = useState<{
    isOpen: boolean;
    field: string;
    title: string;
    value: string;
  }>({
    isOpen: false,
    field: '',
    title: '',
    value: '',
  });
  const [editInsectModal, setEditInsectModal] = useState<{
    isOpen: boolean;
    insectType: 'beetle' | 'moth' | 'weevil' | 'other';
    currentCount: number;
  } | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isFunctionsExpanded, setIsFunctionsExpanded] = useState(false);
  const [showCleanTrapModal, setShowCleanTrapModal] = useState(false);
  const [showDeleteCurrentModal, setShowDeleteCurrentModal] = useState(false);
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [showRemoveTrapModal, setShowRemoveTrapModal] = useState(false);
  const [isPrevDataExpanded, setIsPrevDataExpanded] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const { userInfo } = useSelector((state: RootState) => state.data);
  // Read isSubAccount from auth slice (set during login)
  const { isSubAccount: isSubFromRedux } = useSelector((state: RootState) => state.auth);
  
  // Double-check with localStorage as fallback (use state to avoid hydration mismatch)
  const [isSubFromStorage, setIsSubFromStorage] = useState(false);
  
  useEffect(() => {
    setIsSubFromStorage(localStorage.getItem('isSubAccount') === 'true');
  }, []);
  
  const isSubAccount = isSubFromRedux || isSubFromStorage;
  
  // Get fahrenheit preference from Redux store
  const fahrenheit = userInfo?.fahrenheit || false;

  // Get current locale for date formatting
  const getDateLocale = () => {
    return i18n.language === 'zh' ? 'zh-CN' : 'en-US';
  };

  // Utility functions for formatting previous data values
  const formatPreviousDate = (timestamp: string) => {
    if (!timestamp || timestamp === 'null' || timestamp === null) return 'None';
    
    try {
      // Handle format: "2025-08-16-07-00-00"
      const parts = timestamp.split('-');
      if (parts.length >= 6) {
        const year = parts[0];
        const month = parts[1];
        const day = parts[2];
        const hour = parts[3];
        const minute = parts[4];
        
        // Create a proper date object
        const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hour), parseInt(minute));
        
        // Check if date is valid
        if (isNaN(date.getTime())) {
          return 'None';
        }
        
        // Format nicely - date only
        return date.toLocaleDateString(getDateLocale(), {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }
      
      // Fallback for other formats
      return 'None';
    } catch {
      return 'None';
    }
  };

  // Helper function to format temperature and humidity values
  const formatValue = (value: string | number | null | undefined, unit = '') => {
    if (value === null || value === undefined || isNaN(Number(value)) || value === 'null') {
      return 'None';
    }
    return `${Math.round(Number(value))}${unit}`;
  };

  const formatTemperatureValue = (tempCelsius: string | number | null | undefined, unit = '') => {
    if (tempCelsius === null || tempCelsius === undefined || isNaN(Number(tempCelsius)) || tempCelsius === 'null') {
      return 'None';
    }
    
    const temp = Number(tempCelsius);
    const convertedTemp = fahrenheit ? (temp * 9/5) + 32 : temp;
    return `${Math.round(convertedTemp)}${unit}`;
  };

  useEffect(() => {
    const fetchProbeData = async () => {
      if (!probeId || !teamName) return;
      
      setIsLoading(true);
      try {
        const queryString = `${probeId}?teamName=${teamName}&filter=id&ascend=true`;
        const result = await dispatch(fetchDataLst(queryString)).unwrap();
        setProbeData(result);
        setCurrentDataIndex(0);
      } catch (error) {
        logger.error('Error fetching probe data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    const fetchTrapDetails = async () => {
      if (!probeId) return;
      
      try {
        const result = await dispatch(fetchTrapInfo(probeId)).unwrap();
        setLocalTrapInfo(result);
      } catch (error) {
        logger.error('Error fetching trap info:', error);
      }
    };

    fetchProbeData();
    fetchTrapDetails();
  }, [probeId, teamName, dispatch]);

  const dataList = probeData?.trap?.dataLst || [];
  const currentData = dataList[currentDataIndex];
  const totalItems = dataList.length;

  const handlePrevData = () => {
    if (currentDataIndex > 0) {
      setCurrentDataIndex(currentDataIndex - 1);
    }
  };

  const handleNextData = () => {
    if (currentDataIndex < totalItems - 1) {
      setCurrentDataIndex(currentDataIndex + 1);
    }
  };

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.5, 3));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.5, 1));
  };

  const handleResetZoom = () => {
    setZoomLevel(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoomLevel > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && zoomLevel > 1) {
      const newX = e.clientX - dragStart.x;
      const newY = e.clientY - dragStart.y;
      setPosition({ x: newX, y: newY });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleWheel = (e: React.WheelEvent) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      if (e.deltaY < 0) {
        handleZoomIn();
      } else {
        handleZoomOut();
      }
    }
  };

  const handleEdit = (field: string, title: string, value: string) => {
    if (!probeId) return;
    setEditModal({
      isOpen: true,
      field,
      title,
      value: value || '',
    });
  };

  const handleSave = async (value: string) => {
    if (!probeId) return;

    let _minutes = '0';
    let _hours = '0';
    let _days = '0';

    // Calculate minutes, hours, and days based on frequency if frequency is being updated
    if (editModal.field === 'frequency') {
      const newFrequency = parseInt(value);
      if (newFrequency === 1) {
        _minutes = '5';
        _hours = '0';
        _days = '0';
      } else if (newFrequency === 6) {
        _minutes = '0';
        _hours = '4';
        _days = '0';
      } else if (newFrequency === 2) {
        _minutes = '0';
        _hours = '8';
        _days = '0';
      } else if (newFrequency === 3) {
        _minutes = '0';
        _hours = '0';
        _days = '1';
      } else if (newFrequency === 4) {
        _minutes = '0';
        _hours = '0';
        _days = '2';
      }
    }

    const updatePayload = {
      id: probeId,
      trapName: editModal.field === 'trapName' ? value : trapInfo?.trap?.trapName || '',
      location: editModal.field === 'location' ? value : trapInfo?.trap?.location || '',
      building: editModal.field === 'building' ? value : trapInfo?.trap?.building || '',
      frequency: editModal.field === 'frequency' ? value : trapInfo?.trap?.frequency || '',
      brightness: editModal.field === 'brightness' ? value : trapInfo?.trap?.brightness || '',
      minutes: editModal.field === 'frequency' ? _minutes : trapInfo?.trap?.minutes || '',
      hours: editModal.field === 'frequency' ? _hours : trapInfo?.trap?.hours || '',
      days: editModal.field === 'frequency' ? _days : trapInfo?.trap?.days || '',
      pwm: trapInfo?.trap?.pwm || '',
    };

    try {
      setIsLoading(true);
      await dispatch(updateTrap(updatePayload)).unwrap();
      // Refresh trap info
      const newTrapInfo = await dispatch(fetchTrapInfo(probeId)).unwrap();
      setLocalTrapInfo(newTrapInfo);
      // Only refresh probe data if it exists
      if (probeData) {
        const newProbeData = await dispatch(fetchDataLst(`${probeId}?teamName=${teamName}&filter=id&ascend=true`)).unwrap();
        setProbeData(newProbeData);
      }
    } catch (error) {
      logger.error('Failed to update trap:', error);
    } finally {
      setIsLoading(false);
      setEditModal({ ...editModal, isOpen: false });
    }
  };

  const handleEditInsectCount = (insectType: 'beetle' | 'moth' | 'weevil' | 'other', currentCount: number) => {
    setEditInsectModal({
      isOpen: true,
      insectType,
      currentCount
    });
  };

  const handleInsectCountSave = async () => {
    if (probeId && teamName) {
      try {
        // Refresh probe data
        const queryString = `${probeId}?teamName=${teamName}&filter=id&ascend=true`;
        const newProbeData = await dispatch(fetchDataLst(queryString)).unwrap();
        setProbeData(newProbeData);
        
        // Refresh trap info
        const newTrapInfo = await dispatch(fetchTrapInfo(probeId)).unwrap();
        setLocalTrapInfo(newTrapInfo);

        // Refresh SmartProbe list
        await dispatch(getScrollInfoV2({
          teamName: teamName,
          filter: 'id',
          ascend: true
        })).unwrap();
      } catch (error) {
        logger.error('Failed to refresh data:', error);
      }
    }
  };

  const handleDelete = async () => {
    if (currentData && probeId && teamName) {
      try {
        await dispatch(deleteTrapPush({
          trapId: probeId,
          fileName: currentData.fileName,
          teamName: teamName
        })).unwrap();
        
        // Refresh the data
        const queryString = `${probeId}?teamName=${teamName}&filter=id&ascend=true`;
        const newProbeData = await dispatch(fetchDataLst(queryString)).unwrap();
        setProbeData(newProbeData);
        
        // Refresh trap info
        const newTrapInfo = await dispatch(fetchTrapInfo(probeId)).unwrap();
        setLocalTrapInfo(newTrapInfo);

        // Refresh SmartProbe list
        await dispatch(getScrollInfoV2({
          teamName: teamName,
          filter: 'id',
          ascend: true
        })).unwrap();

        setShowDeleteModal(false);
      } catch (error) {
        logger.error('Error deleting data:', error);
      }
    }
  };

  const handleCleanTrap = async (sessionName?: string) => {
    if (probeId && teamName && sessionName) {
      try {
        logger.log('Starting clean trap process...');
        logger.log('Cleaning trap with session name:', sessionName);
        
        await dispatch(cleanTrap({
          trapId: probeId,
          sessionName: sessionName,
          teamName: teamName
        })).unwrap();
        
        // Force a delay to ensure the server has processed the clean
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Refresh SmartProbe Details
        logger.log('Refreshing probe data...');
        const queryString = `${probeId}?teamName=${teamName}&filter=id&ascend=true`;
        const newProbeData = await dispatch(fetchDataLst(queryString)).unwrap();
        setProbeData(newProbeData);
        
        // Refresh trap info
        logger.log('Refreshing trap info...');
        const newTrapInfo = await dispatch(fetchTrapInfo(probeId)).unwrap();
        setLocalTrapInfo(newTrapInfo);

        // Refresh SmartProbe list
        logger.log('Refreshing SmartProbe list...');
        await dispatch(getScrollInfoV2({
          teamName: teamName,
          filter: 'id',
          ascend: true
        })).unwrap();

        logger.log('Clean trap process complete');
        setShowCleanTrapModal(false);
      } catch (error) {
        logger.error('Error in clean trap process:', error);
      }
    }
  };

  const handleDeleteCurrent = async () => {
    if (probeId && teamName) {
      try {
        await dispatch(deleteCurrentData({
          trapId: probeId,
          teamName: teamName
        })).unwrap();
        
        logger.log('Delete successful, starting refresh...');
        
        // Force a delay to ensure the server has processed the delete
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Refresh SmartProbe Details
        logger.log('Refreshing probe data...');
        const queryString = `${probeId}?teamName=${teamName}&filter=id&ascend=true`;
        const newProbeData = await dispatch(fetchDataLst(queryString)).unwrap();
        logger.log('New probe data:', newProbeData);
        setProbeData(newProbeData);
        
        // Refresh trap info
        logger.log('Refreshing trap info...');
        const newTrapInfo = await dispatch(fetchTrapInfo(probeId)).unwrap();
        logger.log('New trap info:', newTrapInfo);
        setLocalTrapInfo(newTrapInfo);

        // Refresh SmartProbe list
        logger.log('Refreshing SmartProbe list...');
        await dispatch(getScrollInfoV2({
          teamName: teamName,
          filter: 'id',
          ascend: true
        })).unwrap();

        // Reset the current data index since we might have deleted the current item
        setCurrentDataIndex(0);
        
        logger.log('Refresh complete');
        setShowDeleteCurrentModal(false);
      } catch (error) {
        logger.error('Error in delete current data:', error);
      }
    }
  };

  const handleDeleteAll = async () => {
    if (probeId && teamName) {
      try {
        await dispatch(deleteAllData({
          trapId: probeId,
          teamName: teamName
        })).unwrap();
        
        logger.log('Delete successful, starting refresh...');
        
        // Force a delay to ensure the server has processed the delete
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Refresh SmartProbe Details
        logger.log('Refreshing probe data...');
        const queryString = `${probeId}?teamName=${teamName}&filter=id&ascend=true`;
        const newProbeData = await dispatch(fetchDataLst(queryString)).unwrap();
        logger.log('New probe data:', newProbeData);
        setProbeData(newProbeData);
        
        // Refresh trap info
        logger.log('Refreshing trap info...');
        const newTrapInfo = await dispatch(fetchTrapInfo(probeId)).unwrap();
        logger.log('New trap info:', newTrapInfo);
        setLocalTrapInfo(newTrapInfo);

        // Refresh SmartProbe list
        logger.log('Refreshing SmartProbe list...');
        await dispatch(getScrollInfoV2({
          teamName: teamName,
          filter: 'id',
          ascend: true
        })).unwrap();

        // Reset the current data index since we might have deleted the current item
        setCurrentDataIndex(0);
        
        logger.log('Refresh complete');
        setShowDeleteAllModal(false);
      } catch (error) {
        logger.error('Error in delete all data process:', error);
      }
    }
  };

  const handleRemoveTrap = async () => {
    if (probeId && teamName) {
      try {
        logger.log('Starting remove trap process...');
        
        // First, delete all data
        await dispatch(deleteAllData({
          trapId: probeId,
          teamName: teamName
        })).unwrap();
        
        // Force a delay to ensure the server has processed the delete
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Then remove the trap
        logger.log('Removing SmartProbe...');
        await dispatch(removeTrap({
          trapId: probeId,
          teamName: teamName
        })).unwrap();
        
        // Refresh SmartProbe list
        logger.log('Refreshing SmartProbe list...');
        await dispatch(getScrollInfoV2({
          teamName: teamName,
          filter: 'id',
          ascend: true
        })).unwrap();

        logger.log('Remove trap process complete');
        setShowRemoveTrapModal(false);
      } catch (error) {
        logger.error('Error in remove trap process:', error);
      }
    }
  };

  if (!probeId) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-2xl font-bold mb-4">{t('dashboard:probeDetails')}</h2>
        <p className="text-gray-800">{t('dashboard:selectProbe')}</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-2xl font-bold mb-4">{t('dashboard:probeDetails')}</h2>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="bg-white shadow rounded-lg p-6 overflow-y-auto" style={{ height: 'calc(100vh - 8rem)' }}>
        <h2 className="text-2xl font-bold mb-4">{t('dashboard:probeDetails')}</h2>
      <div className="space-y-4">
        <div className="mb-6">
          {/* Horizontal Info Bar */}
          <div className="flex items-center justify-between mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center space-x-8">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center space-x-2">
                    <p className="text-sm text-gray-800">{t('dashboard:smartProbeName')}</p>
                    {!isSubAccount && (
                      <button
                        onClick={() => handleEdit('trapName', t('dashboard:smartProbeName'), trapInfo?.trap?.trapName || '')}
                        className="text-gray-400 hover:text-gray-900 cursor-pointer"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                        </svg>
                      </button>
                    )}
                  </div>
                  <p className="text-lg font-medium">{trapInfo?.trap?.trapName === 'None' || !trapInfo?.trap?.trapName ? probeId : trapInfo?.trap?.trapName}</p>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center space-x-2">
                    <p className="text-sm text-gray-800">{t('dashboard:location')}</p>
                    {!isSubAccount && (
                      <button
                        onClick={() => handleEdit('location', t('dashboard:location'), trapInfo?.trap?.location || '')}
                        className="text-gray-400 hover:text-gray-900 cursor-pointer"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                        </svg>
                      </button>
                    )}
                  </div>
                  <p className="text-lg">{trapInfo?.trap?.location || currentData?.location || 'None'}</p>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center space-x-2">
                    <p className="text-sm text-gray-800">{t('dashboard:building')}</p>
                    {!isSubAccount && (
                      <button
                        onClick={() => handleEdit('building', t('dashboard:building'), trapInfo?.trap?.building || '')}
                        className="text-gray-400 hover:text-gray-900 cursor-pointer"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                        </svg>
                      </button>
                    )}
                  </div>
                  <p className="text-lg">{trapInfo?.trap?.building || currentData?.building || 'None'}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-6">
              {currentData?.wifiInfo ? (
                <WifiIndicator iconvalue={currentData.wifiInfo} size={36} />
              ) : null}
              {currentData?.batteryInfo ? (
                <BatteryIndicator 
                  count={currentData.batteryInfo} 
                  model={currentData.probeModel}
                  size={48}
                  showPercentage={false}
                />
              ) : null}
              <InsectCountIndicator 
                count={currentData?.particleCount}
                insectCollection={currentData?.insectCollection}
                yellowThreshold={userInfo?.yellow || 10}
                lightRedThreshold={userInfo?.lightRed || 20}
                darkRedThreshold={userInfo?.darkRed || 30}
                size={32}
                showCount={true}
              />
            </div>
          </div>

          {/* Image, Navigation, and Data Summary Layout */}
          <div className="flex space-x-8">
            {/* Image and Navigation */}
            <div className="flex flex-col items-center">
              <div className="relative mb-6">
                {currentData?.originalFileUrl ? (
                  <img 
                    src={currentData.originalFileUrl} 
                    alt="Probe image" 
                    width={256}
                    height={256}
                    className="h-64 w-64 rounded-full object-cover cursor-pointer transition-transform duration-200 hover:scale-105"
                    onClick={() => setIsImageModalOpen(true)}
                  />
                ) : (
                  <img 
                    src={fallbackIcon} 
                    alt="No image available" 
                    width={256}
                    height={256}
                    className="h-64 w-64 rounded-full object-cover"
                  />
                )}
              </div>
              
              <div className="flex justify-center space-x-4">
                <button 
                  onClick={handlePrevData}
                  disabled={currentDataIndex === 0}
                  className={`px-3 py-1 rounded-md text-sm font-medium cursor-pointer ${
                    currentDataIndex === 0 
                      ? 'bg-gray-200 text-gray-800 cursor-not-allowed' 
                      : 'bg-blue-500 text-white hover:bg-blue-600'
                  }`}
                >
                  {t('dashboard:prev')}
                </button>
                
                <div className="flex items-center">
                  <input 
                    type="range"
                    min="0"
                    max={Math.max(0, totalItems - 1)}
                    value={currentDataIndex}
                    onChange={(e) => setCurrentDataIndex(parseInt(e.target.value))}
                    className="w-32 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                </div>
                
                <button 
                  onClick={handleNextData}
                  disabled={currentDataIndex === totalItems - 1}
                  className={`px-3 py-1 rounded-md text-sm font-medium cursor-pointer ${
                    currentDataIndex === totalItems - 1 
                      ? 'bg-gray-200 text-gray-800 cursor-not-allowed' 
                      : 'bg-blue-500 text-white hover:bg-blue-600'
                  }`}
                >
                  {t('dashboard:next')}
                </button>
              </div>
              <div className="text-center text-sm text-gray-900 mt-2">
                {totalItems > 0 ? `${currentDataIndex + 1} ${t('dashboard:of')} ${totalItems}` : t('dashboard:noDataAvailableText')}
              </div>
            </div>

            {/* Data Summary */}
            <div className="flex-1 bg-gray-50 rounded-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">{t('dashboard:dataSummary')}</h3>
                {/* Hide delete button for sub-accounts */}
                {!isSubAccount && (
                  <button
                    onClick={() => setShowDeleteModal(true)}
                    className="text-gray-400 hover:text-red-600 transition-colors cursor-pointer"
                    title={t('dashboard:deleteCurrentImageData')}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-800">{t('dashboard:date')}</p>
                    <p className="text-base">
                      {currentData?.createdAt ? new Date(currentData.createdAt).toLocaleDateString(undefined, {
                        year: 'numeric',
                        month: 'numeric',
                        day: 'numeric'
                      }) : 'None'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-800">{t('dashboard:time')}</p>
                    <p className="text-base">
                      {currentData?.createdAt ? new Date(currentData.createdAt).toLocaleTimeString(undefined, {
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                      }) : 'None'}
                    </p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-800">{t('dashboard:temperature')}</p>
                    <p className="text-base">
                      {currentData?.temperature ? 
                        fahrenheit ? 
                          `${Math.round((9 / 5) * parseFloat(currentData.temperature) + 32)}°F` : 
                          `${Math.round(parseFloat(currentData.temperature))}°C` 
                        : 'None'
                      }
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-800">{t('dashboard:relativeHumidity')}</p>
                    <p className="text-base">{currentData?.humidity ? `${Math.round(parseFloat(currentData.humidity))}%` : 'None'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-800">{t('dashboard:totalInsectCount')}</p>
                    <div 
                      className="p-2 border border-gray-300 rounded-md bg-white text-base cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                      onClick={() => setIsCountExpanded(!isCountExpanded)}
                    >
                      <div className="flex justify-between items-center">
                        <span>
                          {currentData?.insectCollection ? 
                            (currentData.insectCollection.beetle || 0) + 
                            (currentData.insectCollection.moth || 0) + 
                            (currentData.insectCollection.weevil || 0) + 
                            (currentData.insectCollection.other || 0) : 0} {t('dashboard:total')}
                        </span>
                        <span className="text-gray-800">
                          {isCountExpanded ? '▼' : '▶'}
                        </span>
                      </div>
                      
                      {isCountExpanded && currentData?.insectCollection && (
                        <div className="mt-2 pt-2 border-t border-gray-200 space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-gray-900">{t('dashboard:moths')}:</span>
                            <div className="flex items-center space-x-2">
                              <span className="font-medium">{currentData.insectCollection?.moth || 0}</span>
                              {!isSubAccount && (
                                <button
                                  onClick={() => handleEditInsectCount('moth', currentData.insectCollection?.moth || 0)}
                                  className="text-gray-400 hover:text-gray-900 cursor-pointer"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-gray-900">{t('dashboard:beetles')}:</span>
                            <div className="flex items-center space-x-2">
                              <span className="font-medium">{currentData.insectCollection?.beetle || 0}</span>
                              {!isSubAccount && (
                                <button
                                  onClick={() => handleEditInsectCount('beetle', currentData.insectCollection?.beetle || 0)}
                                  className="text-gray-400 hover:text-gray-900 cursor-pointer"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-gray-900">{t('dashboard:weevils')}:</span>
                            <div className="flex items-center space-x-2">
                              <span className="font-medium">{currentData.insectCollection?.weevil || 0}</span>
                              {!isSubAccount && (
                                <button
                                  onClick={() => handleEditInsectCount('weevil', currentData.insectCollection?.weevil || 0)}
                                  className="text-gray-400 hover:text-gray-900 cursor-pointer"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-gray-900">{t('dashboard:other')}:</span>
                            <div className="flex items-center space-x-2">
                              <span className="font-medium">{currentData.insectCollection?.other || 0}</span>
                              {!isSubAccount && (
                                <button
                                  onClick={() => handleEditInsectCount('other', currentData.insectCollection?.other || 0)}
                                  className="text-gray-400 hover:text-gray-900 cursor-pointer"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* More SmartProbe Information - Collapsible Section */}
        <div className="mt-6">
          <div className="p-2 border border-gray-300 rounded-md bg-white">
            <div 
              className="flex justify-between items-center cursor-pointer hover:bg-gray-50 transition-colors duration-200 p-2 rounded-md"
              onClick={() => setIsMoreInfoExpanded(!isMoreInfoExpanded)}
            >
              <h4 className="text-md font-medium text-gray-900">{t('dashboard:moreSmartProbeInfo')}</h4>
              <span className="text-gray-800">
                {isMoreInfoExpanded ? '▼' : '▶'}
              </span>
            </div>
            
            {isMoreInfoExpanded && (
              <div className="mt-3 pt-3 border-t border-gray-200 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-800">{t('dashboard:smartProbeId')}</p>
                  <p className="text-base">{probeId || 'None'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-800">{t('dashboard:probeModel')}</p>
                  <p className="text-base">{trapInfo?.trap.probeModel || 'None'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-800">{t('dashboard:macAddress')}</p>
                  <p className="text-base">{trapInfo?.trap.macAddress || 'None'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-800">{t('dashboard:firmwareVersion')}</p>
                  <p className="text-base">{currentData?.firmwareVersion || 'None'}</p>
                </div>
                <div>
                  <div className="flex items-center space-x-2">
                    <p className="text-sm text-gray-800">{t('dashboard:frequency')}</p>
                    {!isSubAccount && (
                      <button
                        onClick={() => handleEdit('frequency', t('dashboard:frequency'), trapInfo?.trap.frequency || '')}
                        className="text-gray-400 hover:text-gray-900 cursor-pointer"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                        </svg>
                      </button>
                    )}
                  </div>
                  <p className="text-base">
                    {(() => {
                      const frequency = trapInfo?.trap.frequency;
                      if (frequency === undefined || frequency === '-1') {
                        return 'None';
                      }
                      switch (frequency) {
                        case '1':
                          return t('dashboard:5minutes');
                        case '2':
                          return t('dashboard:8hours');
                        case '3':
                          return t('dashboard:24hours');
                        case '4':
                          return t('dashboard:48hours');
                        case '5':
                          return t('dashboard:72hours');
                        case '6':
                          return t('dashboard:4hours');
                        default:
                          return 'None';
                      }
                    })()}
                  </p>
                </div>
                <div>
                  <div className="flex items-center space-x-2">
                    <p className="text-sm text-gray-800">{t('dashboard:brightness')}</p>
                    {!isSubAccount && (
                      <button
                        onClick={() => handleEdit('brightness', t('dashboard:brightness'), trapInfo?.trap.brightness || '')}
                        className="text-gray-400 hover:text-gray-900 cursor-pointer"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                        </svg>
                      </button>
                    )}
                  </div>
                  <p className="text-base">{trapInfo?.trap.brightness || 'None'}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* SmartProbe Data Functions - Collapsible Section - Hidden for sub-accounts */}
        {!isSubAccount && (
          <div className="mt-2">
            <div className="p-2 border border-gray-300 rounded-md bg-white">
              <div 
                className="flex justify-between items-center cursor-pointer hover:bg-gray-50 transition-colors duration-200 p-2 rounded-md"
                onClick={() => setIsFunctionsExpanded(!isFunctionsExpanded)}
              >
                <h4 className="text-md font-medium text-gray-900">{t('dashboard:smartProbeDataFunctions')}</h4>
                <span className="text-gray-800">
                  {isFunctionsExpanded ? '▼' : '▶'}
                </span>
              </div>
              
              {isFunctionsExpanded && (
                <div className="mt-3 pt-3 border-t border-gray-200 space-y-2">
                  <button
                    onClick={() => setShowCleanTrapModal(true)}
                    className="w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-gray-50 rounded-md cursor-pointer flex items-center space-x-2"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                    </svg>
                    <span>{t('dashboard:storeDataSession')}</span>
                  </button>
                  <button
                    onClick={() => setShowDeleteCurrentModal(true)}
                    className="w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-gray-50 rounded-md cursor-pointer flex items-center space-x-2"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-yellow-500" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <span>{t('dashboard:deleteAllCurrent')}</span>
                  </button>
                  <button
                    onClick={() => setShowDeleteAllModal(true)}
                    className="w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-gray-50 rounded-md cursor-pointer flex items-center space-x-2"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <span>{t('dashboard:deleteAllStored')}</span>
                  </button>
                  <button
                    onClick={() => setShowRemoveTrapModal(true)}
                    className="w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-gray-50 rounded-md cursor-pointer flex items-center space-x-2"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-600" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                    <span>{t('dashboard:removeSmartProbe')}</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Previous Date Data - Collapsible Section */}
        <div className="mt-2">
          <div className="p-2 border border-gray-300 rounded-md bg-white">
            <div 
              className="flex justify-between items-center cursor-pointer hover:bg-gray-50 transition-colors duration-200 p-2 rounded-md"
              onClick={() => setIsPrevDataExpanded(!isPrevDataExpanded)}
            >
              <h4 className="text-md font-medium text-gray-900">{t('dashboard:previousDateData')}</h4>
              <span className="text-gray-800">
                {isPrevDataExpanded ? '▼' : '▶'}
              </span>
            </div>
            
            {isPrevDataExpanded && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                {currentData && currentData.previousDateTimeStamp ? (
                  <>
                    {/* Previous Date */}
                    <div className="flex items-center justify-center bg-gray-50 px-4 py-2 mb-4 rounded-md">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500 mr-2" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                      </svg>
                      <span className="font-medium text-gray-900">
                        {t('dashboard:previousDate')}: {formatPreviousDate(currentData.previousDateTimeStamp)}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Temperature Data */}
                      <div className="space-y-3">
                        <div className="bg-gray-50 p-3 rounded-md border-l-4 border-gray-400">
                          <p className="text-sm font-medium text-gray-900">{t('dashboard:averageTemperature')}</p>
                          <p className="text-lg font-bold text-gray-900">
                            {formatTemperatureValue(currentData.avgTemperature, `°${fahrenheit ? 'F' : 'C'}`)}
                          </p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-md border-l-4 border-gray-400">
                          <p className="text-sm font-medium text-gray-900">{t('dashboard:maximumTemperature')}</p>
                          <p className="text-lg font-bold text-gray-900">
                            {formatTemperatureValue(currentData.maxTemperature, `°${fahrenheit ? 'F' : 'C'}`)}
                          </p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-md border-l-4 border-gray-400">
                          <p className="text-sm font-medium text-gray-900">{t('dashboard:minimumTemperature')}</p>
                          <p className="text-lg font-bold text-gray-900">
                            {formatTemperatureValue(currentData.minTemperature, `°${fahrenheit ? 'F' : 'C'}`)}
                          </p>
                        </div>
                      </div>
                      
                      {/* Humidity Data */}
                      <div className="space-y-3">
                        <div className="bg-gray-50 p-3 rounded-md border-l-4 border-gray-400">
                          <p className="text-sm font-medium text-gray-900">{t('dashboard:averageHumidity')}</p>
                          <p className="text-lg font-bold text-gray-900">
                            {formatValue(currentData.avgHumidity, '%')}
                          </p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-md border-l-4 border-gray-400">
                          <p className="text-sm font-medium text-gray-900">{t('dashboard:maximumHumidity')}</p>
                          <p className="text-lg font-bold text-gray-900">
                            {formatValue(currentData.maxHumidity, '%')}
                          </p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-md border-l-4 border-gray-400">
                          <p className="text-sm font-medium text-gray-900">{t('dashboard:minimumHumidity')}</p>
                          <p className="text-lg font-bold text-gray-900">
                            {formatValue(currentData.minHumidity, '%')}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Information Note */}
                    <div className="mt-6 bg-blue-50 border border-blue-200 rounded-md p-4">
                      <div className="flex items-start">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        <div className="text-sm text-blue-800">
                          <p className="font-medium mb-1">{t('dashboard:aboutPreviousData')}</p>
                          <p>
                            {t('dashboard:previousDataDescription')}
                          </p>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center justify-center bg-gray-50 px-4 py-8 rounded-md">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-400 mr-2" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                      <span className="text-gray-900 font-medium">
                        {t('dashboard:noPreviousData')}
                      </span>
                    </div>
                    
                    {/* Information Note for no data case */}
                    <div className="mt-4 bg-blue-50 border border-blue-200 rounded-md p-4">
                      <div className="flex items-start">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        <div className="text-sm text-blue-800">
                          <p className="font-medium mb-1">{t('dashboard:aboutPreviousData')}</p>
                          <p>
                            {t('dashboard:previousDataDescription')}
                          </p>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      </div>


      {/* Image Modal with Zoom Controls */}
      {isImageModalOpen && currentData?.originalFileUrl && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" 
          onClick={() => setIsImageModalOpen(false)}
        >
          <div 
            className="relative max-w-4xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="absolute top-2 right-2 flex space-x-2 z-10">
              <button 
                className="bg-white rounded-full p-1 shadow-md cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={handleZoomIn}
                title="Zoom In"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-900" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </button>
              <button 
                className="bg-white rounded-full p-1 shadow-md cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={handleZoomOut}
                title="Zoom Out"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-900" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                </svg>
              </button>
              <button 
                className="bg-white rounded-full p-1 shadow-md cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={handleResetZoom}
                title="Reset Zoom"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-900" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
              <button 
                className="bg-white rounded-full p-1 shadow-md cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => setIsImageModalOpen(false)}
                title="Close"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-900" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div 
              className="overflow-auto h-[80vh] flex items-center justify-center"
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onWheel={handleWheel}
            >
              <div className="w-[80vh] h-[80vh] rounded-full overflow-hidden">
                <img 
                  ref={imageRef}
                  src={currentData.originalFileUrl} 
                  alt="Probe image enlarged"
                  width={800}
                  height={800}
                  className="w-full h-full object-cover shadow-lg transition-transform duration-100"
                  style={{ 
                    transform: `scale(${zoomLevel}) translate(${position.x}px, ${position.y}px)`,
                    cursor: zoomLevel > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'
                  }}
                />
              </div>
            </div>
            
            <div className="absolute bottom-2 left-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
              Zoom: {Math.round(zoomLevel * 100)}% | Use Ctrl+Scroll to zoom | Drag to pan when zoomed
            </div>
          </div>
        </div>
      )}

      <EditModal
        isOpen={editModal.isOpen}
        onClose={() => setEditModal({ ...editModal, isOpen: false })}
        title={editModal.title}
        currentValue={editModal.value}
        onSave={handleSave}
      />

      {editInsectModal && (
        <EditInsectCountModal
          isOpen={editInsectModal.isOpen}
          onClose={() => setEditInsectModal(null)}
          insectType={editInsectModal.insectType}
          currentCount={editInsectModal.currentCount}
          trapId={probeId || ''}
          fileName={currentData?.fileName || ''}
          onSave={handleInsectCountSave}
        />
      )}

      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        probeId={probeId || ''}
        currentData={currentData}
      />

      {/* Confirmation Modals */}
      <FunctionConfirmationModal
        isOpen={showCleanTrapModal}
        onClose={() => setShowCleanTrapModal(false)}
        onConfirm={handleCleanTrap}
        title="Store Data & Delete Current Data"
        description="This will store the current data into a session and delete the current data. This action cannot be undone."
        probeId={probeId || ''}
        requiresSessionName={true}
      />

      <FunctionConfirmationModal
        isOpen={showDeleteCurrentModal}
        onClose={() => setShowDeleteCurrentModal(false)}
        onConfirm={handleDeleteCurrent}
        title="Delete All Current Data"
        description="This will delete all current data. This action cannot be undone."
        probeId={probeId || ''}
      />

      <FunctionConfirmationModal
        isOpen={showDeleteAllModal}
        onClose={() => setShowDeleteAllModal(false)}
        onConfirm={handleDeleteAll}
        title="Delete All Stored & Current Data"
        description="This will delete all stored and current data. This action cannot be undone."
        probeId={probeId || ''}
      />

      <FunctionConfirmationModal
        isOpen={showRemoveTrapModal}
        onClose={() => setShowRemoveTrapModal(false)}
        onConfirm={handleRemoveTrap}
        title="Remove SmartProbe"
        description="This will remove the SmartProbe from the system and delete all associated data (stored and current). This action cannot be undone."
        probeId={probeId || ''}
      />
    </React.Fragment>
  );
}
